import React, { useCallback, useEffect, useState } from 'react';
import api from '../../utils/api';
import { useToast } from '../../context/ToastContext';
import DataTableCard from '../../Components/Table/DataTableCard';

const METRICS = [
  { code: 'CLP_PARTICIPANTS', label: 'Peserta CLP aktif', unit: 'peserta', hint: 'Peserta berstatus aktif pada cohort yang mulai di tahun ini.' },
  { code: 'PROVINCES_REACHED', label: 'Provinsi terjangkau', unit: 'provinsi', hint: 'Provinsi berbeda dari domisili (saat ini) peserta tersebut.' },
  { code: 'REGENCIES_REACHED', label: 'Kabupaten/kota terjangkau', unit: 'kab/kota', hint: 'Kabupaten/kota berbeda dari domisili (saat ini) peserta tersebut.' },
];

const STATUS_BADGE = {
  ACTIVE: { cls: 'bg-success text-success', label: 'Aktif' },
  EXPIRED: { cls: 'bg-warning text-warning', label: 'Kedaluwarsa' },
  REVOKED: { cls: 'bg-secondary text-secondary', label: 'Dicabut' },
};

const formatDateTime = (iso) => {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('id-ID', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

// ---------------------------------------------------------------------------
// API keys
// ---------------------------------------------------------------------------
function ApiKeysSection() {
  const { showToast, showConfirm } = useToast();
  const [keys, setKeys] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ clientName: '', expiresInDays: 90 });
  const [saving, setSaving] = useState(false);
  const [issued, setIssued] = useState(null); // { token, clientName, expiresAt } -- hanya di memori
  const [copied, setCopied] = useState(false);

  const loadKeys = useCallback(async () => {
    try {
      setKeys(await api.getApiKeys());
    } catch (err) {
      console.error(err);
      showToast('Gagal memuat daftar API key.', 'danger');
      setKeys([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  const handleIssue = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = await api.createApiKey({
        clientName: form.clientName,
        expiresInDays: Number(form.expiresInDays),
      });
      setIssued(data);
      setCopied(false);
      setShowForm(false);
      setForm({ clientName: '', expiresInDays: 90 });
      loadKeys();
    } catch (err) {
      showToast(`Gagal menerbitkan key: ${err.message || err}`, 'danger');
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(issued.token);
      setCopied(true);
    } catch (err) {
      showToast('Gagal menyalin. Salin token secara manual.', 'warning');
    }
  };

  const handleRevoke = async (key) => {
    const confirmed = await showConfirm(
      `Cabut key "${key.clientName}" (${key.keyPrefix}…)? Aplikasi yang memakainya langsung ditolak (401). Tindakan ini tidak dapat dibatalkan.`,
      'Cabut API Key',
      'danger',
    );
    if (!confirmed) return;

    try {
      await api.revokeApiKey({ id: key.id });
      showToast('API key berhasil dicabut.', 'success');
      loadKeys();
    } catch (err) {
      showToast(`Gagal mencabut key: ${err.message || err}`, 'danger');
    }
  };

  return (
    <DataTableCard
      icon="bi-key-fill"
      title="API Key"
      subtitle="Kelola key untuk akses data agregat secara baca-saja oleh aplikasi lain."
      actions={(
        <button type="button" className="btn btn-primary rounded-pill px-3" onClick={() => setShowForm(true)}>
          <i className="bi bi-plus-circle me-2"></i>Terbitkan Key
        </button>
      )}
    >
      {issued && (
        <div className="alert alert-warning border-0 rounded-4 mb-4" role="alert">
          <div className="fw-bold mb-1">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            Simpan token ini sekarang. Token tidak akan ditampilkan lagi.
          </div>
          <div className="small text-muted mb-2">
            Klien: {issued.clientName} · berlaku sampai {formatDateTime(issued.expiresAt)}. Simpan di server aplikasi klien (variabel lingkungan), jangan di kode browser atau repositori.
          </div>
          <div className="input-group">
            <input type="text" readOnly className="form-control font-monospace small" value={issued.token} onFocus={(e) => e.target.select()} />
            <button type="button" className="btn btn-dark" onClick={handleCopy}>
              <i className={`bi ${copied ? 'bi-check2' : 'bi-clipboard'} me-2`}></i>{copied ? 'Tersalin' : 'Salin'}
            </button>
          </div>
          <button type="button" className="btn btn-link btn-sm text-muted px-0 mt-2" onClick={() => setIssued(null)}>
            Saya sudah menyimpan token, tutup
          </button>
        </div>
      )}

      <div className="table-responsive">
        <table className="table table-hover align-middle mb-0">
          <thead className="bg-light text-muted small text-uppercase fw-bold">
            <tr>
              <th className="ps-4 py-3 border-0">Klien</th>
              <th className="border-0 py-3">Key</th>
              <th className="border-0 py-3">Status</th>
              <th className="border-0 py-3">Berlaku sampai</th>
              <th className="border-0 py-3">Terakhir dipakai</th>
              <th className="border-0 py-3">Diterbitkan oleh</th>
              <th className="border-0 py-3 text-end pe-4">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {!keys ? (
              Array.from({ length: 3 }).map((_, idx) => (
                <tr key={idx} className="placeholder-glow">
                  {Array.from({ length: 7 }).map((__, c) => (
                    <td key={c} className="py-3"><span className="placeholder col-8 rounded"></span></td>
                  ))}
                </tr>
              ))
            ) : keys.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center py-5">
                  <div className="text-muted mb-3 opacity-25"><i className="bi bi-key fs-1"></i></div>
                  <h6 className="fw-bold text-dark mb-1">Belum ada API key</h6>
                  <p className="text-muted small mb-0">Terbitkan key untuk menghubungkan aplikasi lain.</p>
                </td>
              </tr>
            ) : (
              keys.map((k) => {
                const badge = STATUS_BADGE[k.status] || STATUS_BADGE.REVOKED;
                return (
                  <tr key={k.id}>
                    <td className="ps-4 py-3 fw-bold text-dark">{k.clientName}</td>
                    <td className="py-3">
                      <span className="extra-small fw-medium text-muted bg-light px-2 py-1 rounded font-monospace">{k.keyPrefix}…</span>
                    </td>
                    <td className="py-3">
                      <span className={`badge ${badge.cls} bg-opacity-10 rounded-pill px-3 py-2`}>{badge.label}</span>
                    </td>
                    <td className="py-3 small">{formatDateTime(k.expiresAt)}</td>
                    <td className="py-3 small">
                      {k.lastUsedAt ? (
                        <>
                          {formatDateTime(k.lastUsedAt)}
                          {k.lastUsedIp && <div className="extra-small text-muted">{k.lastUsedIp}</div>}
                        </>
                      ) : <span className="text-muted">Belum pernah</span>}
                    </td>
                    <td className="py-3 small">{k.createdByName}</td>
                    <td className="py-3 pe-4 text-end">
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm rounded-pill px-3"
                        disabled={k.status === 'REVOKED'}
                        onClick={() => handleRevoke(k)}
                      >
                        <i className="bi bi-slash-circle me-1"></i>Cabut
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {showForm && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered">
              <form className="modal-content border-0 rounded-4 shadow" onSubmit={handleIssue}>
                <div className="modal-header border-0 pb-0">
                  <h5 className="modal-title fw-bold">Terbitkan API Key</h5>
                  <button type="button" className="btn-close" aria-label="Tutup" onClick={() => setShowForm(false)}></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label htmlFor="apiKeyClient" className="form-label small fw-medium">Nama klien</label>
                    <input
                      id="apiKeyClient"
                      type="text"
                      className="form-control"
                      placeholder="Nama aplikasi atau klien"
                      maxLength={100}
                      required
                      autoFocus
                      value={form.clientName}
                      onChange={(e) => setForm({ ...form, clientName: e.target.value })}
                    />
                    <div className="form-text">Satu key per klien, supaya bisa dicabut tanpa mengganggu klien lain.</div>
                  </div>
                  <div className="mb-1">
                    <label htmlFor="apiKeyDays" className="form-label small fw-medium">Masa berlaku (hari)</label>
                    <input
                      id="apiKeyDays"
                      type="number"
                      className="form-control"
                      min={1}
                      max={365}
                      required
                      value={form.expiresInDays}
                      onChange={(e) => setForm({ ...form, expiresInDays: e.target.value })}
                    />
                    <div className="form-text">Maks. 365 hari, disarankan 90. Rotasi: terbitkan key baru, pasang, lalu cabut yang lama.</div>
                  </div>
                </div>
                <div className="modal-footer border-0 pt-0">
                  <button type="button" className="btn btn-light rounded-pill px-4" onClick={() => setShowForm(false)}>Batal</button>
                  <button type="submit" className="btn btn-primary rounded-pill px-4" disabled={saving}>
                    {saving ? 'Menerbitkan…' : 'Terbitkan'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}
    </DataTableCard>
  );
}

// ---------------------------------------------------------------------------
// Targets
// ---------------------------------------------------------------------------
function TargetsSection() {
  const { showToast } = useToast();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [values, setValues] = useState(null); // { [metricCode]: string }
  const [savingCode, setSavingCode] = useState(null);

  const loadTargets = useCallback(async (y) => {
    setValues(null);
    try {
      const data = await api.getExecutiveTargets({ year: y });
      setValues(Object.fromEntries(data.targets.map((t) => [t.metricCode, t.targetValue ?? ''])));
    } catch (err) {
      console.error(err);
      showToast('Gagal memuat target.', 'danger');
      setValues({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadTargets(year);
  }, [year, loadTargets]);

  const handleSave = async (metric) => {
    setSavingCode(metric.code);
    try {
      await api.saveExecutiveTarget({
        year,
        metricCode: metric.code,
        targetValue: Number(values[metric.code]),
      });
      showToast(`Target ${metric.label} ${year} disimpan.`, 'success');
    } catch (err) {
      showToast(`Gagal menyimpan target: ${err.message || err}`, 'danger');
    } finally {
      setSavingCode(null);
    }
  };

  const yearOptions = [currentYear - 1, currentYear, currentYear + 1];

  return (
    <DataTableCard
      icon="bi-bullseye"
      title="Target Tahunan"
      subtitle="Dasar penilaian status (hijau/kuning/merah) pada data yang dibagikan lewat API. Periode Jan-Des; KPI tanpa target tampil tanpa status."
      actions={(
        <select
          className="form-select form-select-sm rounded-pill shadow-sm border-0 bg-light px-3"
          style={{ minWidth: '120px', fontSize: '0.8rem' }}
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          aria-label="Tahun target"
        >
          {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      )}
    >
      <div className="row g-4">
        {METRICS.map((m) => (
          <div className="col-lg-4" key={m.code}>
            <div className="border rounded-4 p-4 h-100">
              <label htmlFor={`target-${m.code}`} className="fw-bold text-dark mb-1">{m.label}</label>
              <p className="text-muted extra-small">{m.hint}</p>
              <div className="input-group">
                <input
                  id={`target-${m.code}`}
                  type="number"
                  min={1}
                  className="form-control"
                  placeholder="Belum diatur"
                  disabled={!values}
                  value={values ? values[m.code] ?? '' : ''}
                  onChange={(e) => setValues({ ...values, [m.code]: e.target.value })}
                />
                <span className="input-group-text small">{m.unit}</span>
              </div>
              <button
                type="button"
                className="btn btn-primary btn-sm rounded-pill px-3 mt-3"
                disabled={!values || !values[m.code] || savingCode === m.code}
                onClick={() => handleSave(m)}
              >
                {savingCode === m.code ? 'Menyimpan…' : 'Simpan'}
              </button>
            </div>
          </div>
        ))}
      </div>
    </DataTableCard>
  );
}

export default function ExecutiveApiPage() {
  return (
    <div className="container-fluid p-0 d-flex flex-column gap-4">
      <ApiKeysSection />
      <TargetsSection />
    </div>
  );
}
